import React, { useRef, useState, useEffect, useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { database } from '../../utils/firebase';
import { ref, get, set, onValue } from 'firebase/database';
import { SettingsContext } from '../../contexts/settings-provider';
import usePrevious from '../../hooks/usePrevious';
import { MdVolumeMute, MdVolumeOff } from 'react-icons/md';

import Loading from '../../components/Loading/Loading';

import Join from './Join';
import Results from './Results';
import Game from './Game';
import Main from '../../components/Main/Main';

import * as Tone from 'tone';
import join from '../../assets/audio/join.mp3';
import start from '../../assets/audio/start.mp3';
import end from '../../assets/audio/end.mp3';
import shoot from '../../assets/audio/shoot.mp3';
import hit from '../../assets/audio/hit.mp3';
import music from '../../assets/audio/music.mp3';

import styles from './Display.module.scss';

const Display = () => {
  const minPlayers = 1;
  const gameDuration = 60 * 1000;
  const playerSize = 0.05;
  const projectileSize = 0.025;
  const playerSpeed = 0.002;
  const projectileSpeed = playerSpeed * 5;
  const hitDamage = 10;
  const projectileDecay = 0.0025;
  const projectileWait = 750;

  const location = useLocation();
  const [settings] = useContext(SettingsContext);

  const [muted, setMuted] = useState(false);

  const [loading, setLoading] = useState(true);
  const [roomCode] = useState(location.search.replace('?code=', ''));
  const [invalidRoom, setInvalidRoom] = useState(false);

  const [ready, setReady] = useState(false);

  const [stage, setStage] = useState('join');
  const [users, setUsers] = useState([]);

  const [controls, setControls] = useState(null);
  const [players, setPlayers] = useState([]);
  const [startTime, setStartTime] = useState(0);
  const previousPlayers = usePrevious(players);
  const projectiles = useRef([]);

  const lastTick = useRef(Date.now());

  const joinAudio = useRef(null);
  const startAudio = useRef(null);
  const endAudio = useRef(null);
  const shootAudio = useRef(null);
  const hitAudio = useRef(null);
  const musicAudio = useRef(null);

  const playAudio = (audio) => {
    if (audio && audio.start) {
      Tone.loaded().then(() => {
        audio.start();
      });
    }
  };

  useEffect(() => {
    joinAudio.current = new Tone.Player(join).toDestination();
    startAudio.current = new Tone.Player(start).toDestination();
    endAudio.current = new Tone.Player(end).toDestination();
    shootAudio.current = new Tone.Player(shoot).toDestination();
    hitAudio.current = new Tone.Player(hit).toDestination();
    musicAudio.current = new Tone.Player(music).toDestination();

    musicAudio.current.set({
      loop: true,
      volume: -6,
      autostart: true,
    });
  }, []);

  useEffect(() => {
    joinAudio.current.mute = muted;
    startAudio.current.mute = muted;
    endAudio.current.mute = muted;
    shootAudio.current.mute = muted;
    hitAudio.current.mute = muted;
    if (muted) {
      if (musicAudio.current) {
        Tone.loaded().then(() => {
          musicAudio.current.stop();
        });
      }
    } else {
      if (musicAudio.current) {
        Tone.loaded().then(() => {
          musicAudio.current.start();
        });
      }
    }
  }, [muted]);

  // Check that user is signed in and that the room exists and is owned by the user
  useEffect(() => {
    if (settings.user) {
      if (roomCode) {
        const roundData = ref(database, `games/${roomCode}/owner`);
        get(roundData)
          .then((snapshot) => {
            if (snapshot.exists() && snapshot.val() === settings.user.uid) {
              setLoading(false);
              setReady(true);
            } else {
              setInvalidRoom(true);
            }
          })
          .catch((error) => {
            setInvalidRoom(true);
          });
      } else {
        setInvalidRoom(true);
      }
    }
  }, [roomCode, settings.user]);

  // Listen for changes in registered users
  useEffect(() => {
    if (ready) {
      const roundData = ref(database, `games/${roomCode}/users`);
      onValue(roundData, (snapshot) => {
        const data = snapshot.val();
        const newUsers = [];
        for (const key in data) {
          if (data[key]?.banned !== true) {
            newUsers.push({
              ...data[key],
              uid: key,
              banned: data[key]?.banned === true,
            });
          }
        }
        playAudio(joinAudio.current);
        setUsers(newUsers);
      });
    }
  }, [ready, roomCode]);

  // Listen for changes in players controls
  useEffect(() => {
    if (ready) {
      const roundData = ref(database, `games/${roomCode}/controls`);
      onValue(roundData, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setControls(data);
        }
      });
    }
  }, [ready, roomCode]);

  // Reset game when entering play
  useEffect(() => {
    const resetGame = () => {
      projectiles.current = [];
      const newPlayers = [];
      users.forEach((user) => {
        if (user.ready) {
          newPlayers.push({
            x: 0.5,
            y: 0.5,
            d: Math.random() * (Math.PI * 2),
            hp: 100,
            score: 0,
            control: 0,
            shooting: false,
            lastShot: Date.now(),
            name: user.name,
            uid: user.uid,
          });
        }
      });
      setStartTime(Date.now());
      setPlayers(newPlayers);
    };

    const playingRef = ref(database, `games/${roomCode}/round/playing`);
    if (stage === 'play') {
      playAudio(startAudio.current);

      resetGame();
      set(playingRef, true);
    } else {
      set(playingRef, false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, roomCode]);

  // Update users when controls are changed
  useEffect(() => {
    if (players) {
      const newPlayers = [...players].map((player) => {
        if (controls[player.uid]) {
          return {
            ...player,
            control: controls[player.uid].orientation / 100,
            shooting: controls[player.uid].shooting,
          };
        } else {
          return player;
        }
      });
      setPlayers(newPlayers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controls]);

  // Update player health in Firebase
  useEffect(() => {
    if (players && previousPlayers) {
      const newHealth = {};
      players.forEach((player) => {
        newHealth[player.uid] = player.hp;
      });
      const previousHealth = {};
      previousPlayers.forEach((player) => {
        previousHealth[player.uid] = player.hp;
      });
      let changed = false;
      for (const key in newHealth) {
        if (newHealth[key] !== previousHealth[key]) {
          changed = true;
        }
      }
      if (changed) {
        const healthRef = ref(database, `games/${roomCode}/health`);
        set(healthRef, newHealth);
      }
    }
  }, [players, previousPlayers, roomCode]);

  // Tick
  useEffect(() => {
    const calculatePlayers = (playersList, timeMod) => {
      const hits = [];

      const newPlayers = [...playersList].map((player) => {
        if (player.hp <= 0) {
          return player;
        }
        const newDirection = player.d + (player.control / 200) * timeMod;
        const angle = newDirection * (Math.PI * 2);
        const newX = (player.x + playerSpeed * timeMod * Math.cos(angle)) % 1;
        const newY = (player.y + playerSpeed * timeMod * Math.sin(angle)) % 1;

        let damage = 0;
        projectiles.current.forEach((projectile, projectileIndex) => {
          if (
            projectile &&
            projectile.x >= player.x - playerSize / 1 &&
            projectile.x <= player.x + playerSize / 1 &&
            projectile.y >= player.y - playerSize / 1 &&
            projectile.y <= player.y + playerSize / 1
          ) {
            if (player.uid === projectile.uid && projectile.tick < 1000) {
            } else {
              playAudio(hitAudio.current);

              hits.push({ target: player.uid, shooter: projectile.uid });
              damage += hitDamage * projectile.size;
              projectiles.current.splice(projectileIndex, 1);
            }
          }
        });

        let shoot = false;
        if (player.shooting && Date.now() - player.lastShot > projectileWait) {
          playAudio(shootAudio.current);

          shoot = true;
          projectiles.current.push({
            x: player.x,
            y: player.y,
            d: player.d,
            size: 1,
            uid: player.uid,
            tick: 0,
          });
        }

        return {
          ...player,
          d: newDirection,
          x: newX < 0 ? newX + 1 : newX,
          y: newY < 0 ? newY + 1 : newY,
          hp: player.hp - damage,
          lastShot: shoot ? Date.now() : player.lastShot,
        };
      });

      newPlayers.forEach((player) => {
        hits.forEach((hit) => {
          const selfInflicted = hit.target === hit.shooter;
          if (player.uid === hit.shooter) {
            player.score += selfInflicted ? -hitDamage : hitDamage;
          }
        });
      });

      return newPlayers;
    };

    const calculateProjectiles = (timeMod) => {
      projectiles.current = projectiles.current.map((projectile) => {
        if (projectile) {
          const angle = projectile.d * (Math.PI * 2);
          const newX =
            (projectile.x + projectileSpeed * timeMod * Math.cos(angle)) % 1;
          const newY =
            (projectile.y + projectileSpeed * timeMod * Math.sin(angle)) % 1;
          const newSize = projectile.size - projectileDecay * timeMod;
          if (newSize > 0) {
            return {
              ...projectile,
              x: newX < 0 ? newX + 1 : newX,
              y: newY < 0 ? newY + 1 : newY,
              size: newSize,
              tick: projectile.tick + 1,
            };
          }
        }
        return null;
      });
    };

    const tick = () => {
      if (stage === 'play') {
        const deltaTime = (Date.now() - lastTick.current) / (1000 / 60);
        calculateProjectiles(deltaTime);
        const updatedPlayers = calculatePlayers(players, deltaTime);
        let livingPlayers = 0;
        updatedPlayers.forEach((player) => {
          if (player.hp > 0) {
            livingPlayers++;
          }
        });
        if (
          livingPlayers <= minPlayers - 1 ||
          Date.now() - startTime >= gameDuration
        ) {
          playAudio(endAudio.current);

          setStage('summary');
        }

        setPlayers(updatedPlayers);

        lastTick.current = Date.now();
      }
    };

    const clock = window.setInterval(tick);
    return () => {
      clearInterval(clock);
    };
  }, [stage, players, startTime, gameDuration, projectiles, projectileSpeed]);

  if (invalidRoom) {
    return <Navigate to={'/'} state={{ error: 'Invalid room' }} />;
  } else if (loading) {
    return <Loading />;
  } else {
    return (
      <Main>
        <button
          className={styles.mute}
          onClick={() => {
            setMuted(!muted);
          }}
        >
          {muted ? <MdVolumeOff /> : <MdVolumeMute />}
        </button>
        {stage === 'join' && (
          <Join
            minPlayers={minPlayers}
            users={users}
            roomCode={roomCode}
            startGame={() => {
              setStage('play');
            }}
          />
        )}
        {stage === 'play' && (
          <Game
            players={players}
            projectiles={projectiles}
            remainingTime={gameDuration - (Date.now() - startTime)}
            projectileSize={projectileSize}
            playerSize={playerSize}
          />
        )}
        {stage === 'summary' && (
          <Results
            players={players}
            newGame={() => {
              setStage('join');
            }}
          />
        )}
      </Main>
    );
  }
};

export default Display;
